/* eslint-disable max-len */
/* eslint-disable space-before-function-paren */
/* eslint-disable camelcase */
/* eslint-disable keyword-spacing */
import ReactGA from 'react-ga4';
import * as actions from 'src/store/user/user.actions';
import * as agencyActions from 'src/store/user-info/user-info.actions';

import {
    userSelectedLocationInfoSelector,
    userRoleSelector,
} from 'src/store/user/user.selectors';
import { userAgenciesSelector } from 'src/store/user-info/user-info.selectors';
import { isTrafficViewerRole } from 'src/constants/roles.constants';
import {
    readUserPreferences,
    readStoredUserPreferencesForState,
    findJurisdictionInAgencies,
    agencyMatchesPreferenceId,
    storedLocationPreferencesAreConsistent,
} from 'src/utils/user-preferences/user-preferences';
import { runWithUserPreferencesRestore } from 'src/store/middleware/user-preferences.middleware';

import { initializeAfterLogin, buildUserAnalyticsProperties } from 'src/store/initialization/initialization.operations';
import { syncFeatureFlagsOperation } from 'src/store/feature-flags/feature-flags.operations';
import { updateUserProperties } from 'src/utils/analytics';
import {
    LOG_LEVEL,
    logger,
} from 'src/logger/logger';
import {
    LOG_EVENT_TYPE,
    createLogEvent,
} from 'src/logger/log-event';
import { createTransactionId } from 'src/logger/transaction-id';

import { getUserAgenciesRequest as fetchUserAgencies } from 'src/services/requests/sidebar/sidebar';
import { getAllAgencyTypes } from 'src/services/requests/cities-and-agencies/cities-and-agencies';
import { normalizeAllAgencyTypes } from 'src/services/requests/cities-and-agencies/normalize-all-agency-types';
import { isLytRole } from 'src/constants/roles.constants';

import { bookList } from 'src/constants/bookList';
import { NAV_REGISTRY } from 'src/config/navigation.registry';
import { getRestrictedRoutePrefixesForAgencyType } from 'src/config/navigation.utils';
import { featureFlagValuesSelector } from 'src/store/feature-flags/feature-flags.selectors';
import { route } from 'src/routing/navigation';

import * as requests from 'src/services/requests/user/user';

const CODE_LOCATION = 'src/store/user/user.operations.js';

const createUserProfileOperationEvent = ({
    transactionId,
    level,
    message,
    outcome,
    bootCanContinue,
}) => createLogEvent({
    eventType: LOG_EVENT_TYPE.OPERATION,
    codeLocation: CODE_LOCATION,
    methodName: 'getUserPersonalInformation',
    level,
    message,
    outcome,
    transactionId,
    attributes: {
        frontend_data: {
            boot_can_continue: bootCanContinue,
            ...(!bootCanContinue
                ? { reason: 'profile_required_for_boot' }
                : {}),
        },
    },
});

/**
 * Fetch agency list for the current user.
 *
 * For LYT-internal roles (lyt_admin, lyt_employee) we call
 *   GET /agencies/alltypes
 * which returns every agency across all types.
 *
 * For all other roles we call
 *   GET /agencies/cities/user
 * which returns only the agencies the user belongs to.
 */
export const getUserAgenciesOperation = () => (dispatch, getState) => {
    dispatch(agencyActions.getUserAgenciesRequest());

    const userRole = userRoleSelector(getState());
    const lytUser = isLytRole(userRole);

    if (lytUser) {
        return getAllAgencyTypes()
            .then((rows) => {
                const agencies = normalizeAllAgencyTypes(rows);
                dispatch(agencyActions.getUserAgenciesSuccess(agencies));
                dispatch(setUserSelectedLocationInitialOperation());
            })
            .catch((error) => {
                dispatch(agencyActions.getUserAgenciesError(error));
            });
    }

    return fetchUserAgencies()
        .then((response) => {
            dispatch(agencyActions.getUserAgenciesSuccess(response));
            dispatch(setUserSelectedLocationInitialOperation());
        })
        .catch((error) => {
            dispatch(agencyActions.getUserAgenciesError(error));
        });
};

export const getUserPersonalInformation = () => async (dispatch) => {
    dispatch(actions.getUserPersonalInformationRequest());
    const transactionId = createTransactionId();

    let response;
    try {
        response = await requests.getUserPersonalInformationRequest({
            transactionId,
        });
    } catch (error) {
        // A profile-fetch failure prevents authenticated application boot, so
        // this thunk classifies it as an operational error. Other thunks must
        // interpret their own statuses (for example, expected validation).
        logger.event(createUserProfileOperationEvent({
            transactionId,
            level: LOG_LEVEL.ERROR,
            message: 'Initial user profile unavailable',
            outcome: 'failure',
            bootCanContinue: false,
        }));
        dispatch(actions.getUserPersonalInformationError(error));
        return;
    }

    logger.event(createUserProfileOperationEvent({
        transactionId,
        level: LOG_LEVEL.INFO,
        message: 'Initial user profile loaded',
        outcome: 'success',
        bootCanContinue: true,
    }));

    dispatch(actions.getUserPersonalInformationSuccess(response));

    const storedPreferences = readUserPreferences(response?.email);
    if (storedPreferences?.theme) {
        runWithUserPreferencesRestore(() => {
            dispatch(actions.setUserTheme(storedPreferences.theme));
        });
    }

    // Initialize application data after successful login. Failures in later
    // boot stages are not user-profile network failures.
    try {
        await dispatch(initializeAfterLogin());
        dispatch(applyStoredUserPreferencesOperation(response?.email));
    } catch (initError) {
        console.warn('Failed to initialize application data during login:', initError);
        // Don't fail the login process if initialization fails
    }
};

export const sendUserUpdateOperation = (data) => (dispatch) => {
    dispatch(actions.setUserPersonalInformationRequest());

    return requests
        .updateUserRequest(data)
        .then(response => {
            dispatch(actions.setUserPersonalInformationSuccess(response));
        })
        .catch((error) => {
            console.log('error', error);
            dispatch(actions.setUserPersonalInformationError(error));
        });
};

export const changePasswordOperation = (data) => async (dispatch) => {
    dispatch(actions.changePasswordRequest());
    try {
        const response = await requests
            .changePasswordRequest(data);
        dispatch(actions.changePasswordSuccess(response));
    } catch (error) {
        dispatch(actions.changePasswordError(error));
    }
};

const applyStoredLocationPreferences = ({
    dispatch,
    list,
    storedPreferences,
    userRole,
}) => {
    const shouldRestoreJurisdictionFirst = storedPreferences?.jurisdictionId
        && storedLocationPreferencesAreConsistent(list, storedPreferences);

    if (shouldRestoreJurisdictionFirst) {
        const storedJurisdiction = findJurisdictionInAgencies(
            list,
            storedPreferences.jurisdictionId,
        );

        if (storedJurisdiction) {
            dispatch(agencyActions.setSelectedJurisdiction(storedJurisdiction.jurisdictionId));
            dispatch(actions.setUserSelectedLocation({
                agencyId: storedJurisdiction.agencyId,
                cityId: storedJurisdiction.jurisdictionId,
            }));
            dispatch(actions.setUserAgencyId(storedJurisdiction.agencyId));
            return true;
        }
    }

    const preferredAgencyId = storedPreferences?.agencyId;
    if (!preferredAgencyId) {
        return false;
    }

    const agency = list.find((entry) => agencyMatchesPreferenceId(
        entry,
        preferredAgencyId,
    ));

    if (!agency) {
        return false;
    }

    const preferredCityId = storedPreferences?.jurisdictionId
        || storedPreferences?.cityId
        || '';
    const cityIsValid = (agency?.jurisdictions || []).some(
        (jurisdiction) => agencyMatchesPreferenceId(jurisdiction, preferredCityId),
    );
    const cityId = cityIsValid
        ? preferredCityId
        : agency?.jurisdictions?.[0]?.id || '';

    dispatch(actions.setUserSelectedLocation({
        agencyId: agency.agencies_id,
        cityId,
    }));
    dispatch(actions.setUserAgencyId(agency.agencies_id));

    if (isTrafficViewerRole(userRole) && cityId) {
        dispatch(agencyActions.setSelectedJurisdiction(cityId));
    }

    return true;
};

export const applyStoredUserPreferencesOperation = (emailOverride) => (dispatch, getStore) => {
    runWithUserPreferencesRestore(() => {
        const state = getStore();
        const { list } = userAgenciesSelector(state);
        if (!list || list.length === 0) {
            return;
        }

        const storedPreferences = emailOverride
            ? readUserPreferences(emailOverride)
            : readStoredUserPreferencesForState(state);

        const { agencyId: currentAgencyId } = userSelectedLocationInfoSelector(state);
        const userRole = userRoleSelector(state);

        if (storedPreferences && applyStoredLocationPreferences({
            dispatch,
            list,
            storedPreferences,
            userRole,
        })) {
            return;
        }

        const isMember = list.some((agency) => agencyMatchesPreferenceId(
            agency,
            currentAgencyId,
        ));
        if (currentAgencyId && isMember) {
            dispatch(actions.setUserAgencyId(currentAgencyId));

            if (isTrafficViewerRole(userRole)) {
                const { cityId } = userSelectedLocationInfoSelector(state);
                if (cityId) {
                    dispatch(agencyActions.setSelectedJurisdiction(cityId));
                }
            }
            return;
        }

        const firstElAgencyId = list[0]?.agencies_id || '';
        const firstElCityId = list[0]?.jurisdictions?.[0]?.id || '';

        dispatch(
            actions.setUserSelectedLocation({
                agencyId: firstElAgencyId,
                cityId: firstElCityId,
            })
        );
        dispatch(actions.setUserAgencyId(firstElAgencyId));

        if (isTrafficViewerRole(userRole) && firstElCityId) {
            dispatch(agencyActions.setSelectedJurisdiction(firstElCityId));
        }
    });
};

export const setUserSelectedLocationInitialOperation = () => (
    dispatch,
    getStore,
) => applyStoredUserPreferencesOperation()(dispatch, getStore);

export const setUserAgencyIdOperation = (agencyId) => async (dispatch, getStore) => {
    ReactGA.gtag('event', 'lyt-agency-change', {
        agency: agencyId,
    });
    const { list } = userAgenciesSelector(getStore());
    const agency = list.find(({ agencies_id }) => agencies_id === agencyId);
    const { jurisdictions } = agency;

    // when we change the agency we need to add the first city from the list
    const firstElCityId = jurisdictions[0]?.id || '';
    const selectedLocationData = { agencyId, cityId: firstElCityId };

    dispatch(actions.setUserSelectedLocation(selectedLocationData));
    dispatch(actions.setUserAgencyId(agencyId));

    const userRole = userRoleSelector(getStore());
    if (isTrafficViewerRole(userRole)) {
        dispatch(agencyActions.setSelectedJurisdiction(firstElCityId));
    } else {
        dispatch(agencyActions.setSelectedJurisdiction(null));
    }

    // Keep analytics user_properties in sync with the new agency so events
    // fired after this point are attributed to the correct agency segment.
    updateUserProperties(buildUserAnalyticsProperties(getStore()));

    await dispatch(syncFeatureFlagsOperation({ isRefresh: true }));

    // If the user switches to an agency whose type restricts the current route,
    // redirect them to Live Operations before re-fetching page data.
    const restrictedPrefixes = getRestrictedRoutePrefixesForAgencyType(
        NAV_REGISTRY,
        agency?.agency_type,
        featureFlagValuesSelector(getStore()),
    );
    if (restrictedPrefixes.length > 0) {
        const currentPath = window.location.pathname;
        const isOnRestrictedRoute = restrictedPrefixes.some(
            (prefix) => currentPath.startsWith(`/${prefix}`)
        );
        if (isOnRestrictedRoute) {
            route(bookList.live_operations.default);
        }
    }

    // Data re-fetching is handled by individual components via useEffect
    // reacting to agencyId changes through userAgencyIdSelector.
};
